import { HttpBackend } from "@angular/common/http";
import { importProvidersFrom } from "@angular/core";
import { Routes } from "@angular/router";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";

import {
	ConfirmComponent,
	LogOutComponent,
	MFAComponent,
	MfaSetupComponent,
	MustSetMainFunctionProblemComponent,
	NoPermissionsComponent,
	PermissionsGuard,
	PrivacyGuard,
	SelectOrganisationComponent,
	SelectOrganisationGuard,
	SignInComponent,
	SignInWithUsernameComponent,
	UnknownProblemComponent,
} from "@intocare/auth";
import { RouteNotFoundComponent } from "@intocare/core";
import { hqEnvironment } from "@intocare/environments";
import { CacheBustMultiTranslateHttpLoader, TranslationLoaderResolverFn } from "@intocare/language";

import { HomeComponent } from "./core/containers/home/home.component";
import {
	AuthenticatedGuard,
	ConfirmPasswordGuard,
	IsAuthenticatedGuard,
	NotAuthenticatedGuard,
	OrganisationGuard,
} from "./core/guards";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ErrorPageComponent } from "../../../../libs/core/src/containers/error-page/error-page.component";

export const routes: Routes = [
	{
		path: "select-organisation",
		component: SelectOrganisationComponent,
		canActivate: [SelectOrganisationGuard],
	},
	{
		path: "no-permission",
		pathMatch: "full",
		component: NoPermissionsComponent,
	},
	{
		path: "unknown-problem",
		pathMatch: "full",
		component: UnknownProblemComponent,
	},
	{
		path: "must-set-main-function",
		pathMatch: "full",
		component: MustSetMainFunctionProblemComponent,
	},
	{
		path: "error",
		pathMatch: "full",
		component: ErrorPageComponent,
	},
	{
		path: ":org",
		canActivate: [OrganisationGuard],
		children: [
			{
				path: "no-permissions",
				component: NoPermissionsComponent,
				canActivate: [IsAuthenticatedGuard],
			},
			{
				path: "sign-in",
				component: SignInComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "log-out",
				component: LogOutComponent,
			},
			{
				path: "confirm",
				component: ConfirmComponent,
				canActivate: [OrganisationGuard, ConfirmPasswordGuard],
			},
			{
				path: "mfa",
				component: MFAComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "sign-in-with-username",
				component: SignInWithUsernameComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "mfa-setup",
				component: MfaSetupComponent,
				canActivate: [OrganisationGuard, AuthenticatedGuard],
			},
			{
				path: "",
				component: HomeComponent,
				canActivate: [OrganisationGuard, AuthenticatedGuard],
				children: [
					{
						path: "",
						canActivate: [PrivacyGuard],
						children: [
							{
								path: "",
								redirectTo: "dashboard",
								pathMatch: "full",
							},
							{
								path: "dashboard",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: async () => (await import("./+dashboard/dashboard.module")).DashboardModule,
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									permissions: ["HQ.DASHBOARD"],
									redirectTo: "../organisation",
								},
							},
							{
								path: "organisation",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: async () => (await import("./+settings/settings.module")).SettingsModule,
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									permissions: {
										$or: [
											"HQ.PHARMACY_LINKS",
											"HQ.PHARMACY",
											"HQ.WOUNDCARE",
											"HQ.DOCUMENT_TEMPLATES",
											"HQ.ALARMS",
											"HQ.CARE_TREE",
											"HQ.ACTIVITIES",
										],
									},
									redirectTo: "../access-rights",
								},
							},
							{
								path: "access-rights",
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									permissions: { $or: ["HQ.USER_MANAGEMENT", "HQ.ACCESS_MANAGEMENT"] },
									redirectTo:
										hqEnvironment.environment === "development" ? "../organisation/configuration/hq" : "../dashboard",
								},
								loadChildren: () => import("@intocare/hq/access-rights/feat-access").then((mod) => mod.accessRoutes),
								resolve: {
									translations: TranslationLoaderResolverFn,
								},
								providers: [
									importProvidersFrom([
										TranslateModule.forChild({
											loader: {
												provide: TranslateLoader,
												useFactory: (httpBackend: HttpBackend) => {
													return new CacheBustMultiTranslateHttpLoader(httpBackend, []);
												},
												deps: [HttpBackend],
											},
											isolate: false,
										}),
									]),
								],
							},
							{
								path: "communication",
								canActivate: [PermissionsGuard],
								data: {
									permissions: ["HQ.INTERNAL_COMMUNICATION"],
									redirectTo: "../dashboard",
								},
								loadChildren: async () => await import("@intocare/features/internal-communication/hq"),
							},
							{ path: "**", component: RouteNotFoundComponent },
						],
					},
				],
			},
		],
	},
	{
		path: "",
		redirectTo: "select-organisation",
		pathMatch: "full",
	},
	{
		path: "**",
		redirectTo: "select-organisation",
	},
];
